import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, AlertTriangle, RotateCcw, Save, Info, ChevronDown, ChevronRight, X } from 'lucide-react';
import { rMoney, rDec, lastFactor, facText } from '../utils/yatasChain';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Cell, CartesianGrid } from 'recharts';

/* ------------------------------------------------------------------ */
/*  Yataş — "Hedeften Plana" sekmesi (/yatas/planlama)                 */
/*  Kaynak: Yataş notu, 18 Eki 2026: "Kasım hedeflerini girsek (trafik, */
/*  ciro, performans bütçesi); 4 videonun X kişiye 3 sıklıkla ulaşması  */
/*  için YouTube'a ne kadar? Branding'de sistem reach hedefi versin."   */
/*  Sistem hedefi önerir → kullanıcı değiştirir → gereken bütçe ve      */
/*  dağılım ters çözülür. Backend: GET /api/yatas-planning/target-plan  */
/*  Ciro/trafik/ROAS tanımları Hedef vs Gerçekleşen sayfasıyla aynı:    */
/*  site geneli GA4; ROAS = site cirosu / performans bütçesi.          */
/* ------------------------------------------------------------------ */

const API = 'https://api.glowytics.com';

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const monthLabel = (ym) => (ym ? `${MONTHS_TR[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}` : '');
const shortMonth = (ym) => (ym ? `${MONTHS_TR[Number(ym.slice(5, 7)) - 1].slice(0, 3)} ${ym.slice(2, 4)}` : '');

const tl = (v) => (v == null ? '—' : `₺${Math.round(v).toLocaleString('tr-TR')}`);
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString('tr-TR'));
const compact = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} Mn`;
  if (a >= 1e3) return `${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} bin`;
  return Math.round(v).toLocaleString('tr-TR');
};
const tlc = (v) => (v == null ? '—' : `₺${compact(v)}`);
// Hesap zinciri için: Mn'de 2 ondalık (₺37,78 Mn) → çarpım/bölme gözle doğrulanabilsin
const tlp = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e6) return `₺${(v / 1e6).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mn`;
  if (a >= 1e3) return `₺${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} bin`;
  return `₺${Math.round(v).toLocaleString('tr-TR')}`;
};
const cpmFmt = (v) => (v == null ? '—' : `₺${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`);
const dec = (v, k = 2) => (v == null ? '—' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: k, maximumFractionDigits: k }));

const FEASIBILITY = {
  uygun: { label: 'Gerçekçi', cls: 'bg-green-50 text-green-700 border-green-200' },
  zorlayici: { label: 'Zorlayıcı', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  gercekci_degil: { label: 'Gerçekçi değil', cls: 'bg-red-50 text-red-700 border-red-200' },
};
const CONFIDENCE = {
  yuksek: { label: 'Tahmin güveni yüksek', cls: 'text-green-700' },
  orta: { label: 'Tahmin güveni orta', cls: 'text-amber-700' },
  dusuk: { label: 'Tahmin güveni düşük', cls: 'text-red-700' },
};

function nextMonthYm(k = 1) {
  const d = new Date();
  const n = new Date(d.getFullYear(), d.getMonth() + k, 1);
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

async function call(path, brandId, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    ...opts,
    headers: { 'X-Brand-ID': brandId, ...(opts.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

/** Sayı girişi: boşken önerilen değer görünür (gri); yazınca kullanıcının değeri olur. */
function TargetInput({ label, value, suggested, suggestedLabel = 'sistem önerisi', onChange, prefix, suffix, decimals = 0, width = 'w-32', hint }) {
  const overridden = value != null;
  const shown = overridden ? value : suggested;
  const fmt = (v) => (v == null ? '' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: decimals }));
  const [text, setText] = useState(fmt(shown));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(fmt(shown)); }, [shown, focused]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (raw) => {
    const clean = decimals ? raw.replace(/\./g, '').replace(',', '.') : raw.replace(/\D/g, '');
    const n = Number(clean);
    onChange(clean === '' || !Number.isFinite(n) || n <= 0 ? null : n);
  };

  return (
    <label className="flex flex-col text-xs text-gray-500 gap-1 min-w-0">
      <span className="flex flex-wrap items-center gap-x-2">
        {label}
        {overridden && suggested != null ? (
          <button type="button" onClick={() => onChange(null)} className="text-[11px] text-blue-600 hover:underline flex items-center gap-0.5">
            <RotateCcw size={10} /> {suggestedLabel}: {decimals ? dec(suggested, 1) : compact(suggested)}
          </button>
        ) : suggested != null ? <span className="text-[11px] text-gray-400">{suggestedLabel}</span> : null}
      </span>
      <div className={`flex items-center border rounded-md px-2 bg-white focus-within:border-blue-400 ${overridden ? 'border-blue-300' : 'border-gray-200'}`}>
        {prefix && <span className="text-gray-400 text-sm">{prefix}</span>}
        <input
          type="text" inputMode={decimals ? 'decimal' : 'numeric'}
          value={text}
          onFocus={() => setFocused(true)}
          onChange={(e) => { setText(e.target.value); commit(e.target.value); }}
          onBlur={() => setFocused(false)}
          className={`${width} min-w-0 px-1 py-1.5 text-sm focus:outline-none ${overridden ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
        />
        {suffix && <span className="text-gray-400 text-xs whitespace-nowrap">{suffix}</span>}
      </div>
      {hint && <span className="text-[11px] text-gray-400 max-w-[260px] leading-snug">{hint}</span>}
    </label>
  );
}

/* ------------------------------ Ortak parçalar ------------------------------ */

// Panel dili (GoogleAdsDonusumKirilimi tokenları): kart 12px, çizgi #E6EAF5, vurgu #436AEB
const C = {
  accent: '#436AEB', accentSoft: '#DCE4FB', brand: '#8B5CF6', brandSoft: '#E9E1FD',
  gray: '#C5CBD8', ink: '#1F2937', line: '#E6EAF5', ly: '#F59E0B',
};
const CARD = 'bg-white border border-[#E6EAF5] rounded-xl';

function ymShift(ym, k) {
  const d = new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + k, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Stat({ label, value, sub, strong }) {
  return (
    <div className="tp-res min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`tabular-nums break-words ${strong ? 'text-3xl font-semibold text-[#436AEB]' : 'text-xl font-semibold text-gray-800'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5 leading-snug">{sub}</div>}
    </div>
  );
}

function SectionHead({ n, title, sub, right }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <span className="shrink-0 w-7 h-7 rounded-full bg-[#436AEB] text-white text-sm font-semibold flex items-center justify-center">{n}</span>
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-900 text-lg leading-tight">{title}</h2>
          {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

/** "Neden bu rakam?" — hesabı adım adım, her adımda rakam + nereden geldiği */
function Steps({ title = 'Bu rakam nasıl çıktı?', items }) {
  const list = items.filter(Boolean);
  return (
    <div className="tp-res rounded-xl bg-[#F6F8FF] border border-[#E6EAF5] p-4">
      <div className="text-xs font-semibold text-gray-700 mb-3">{title}</div>
      <ol className={`grid grid-cols-1 sm:grid-cols-2 ${list.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-3`}>
        {list.map((s, i) => (
          <li key={s.title} className={`relative bg-white rounded-lg border p-3 ${s.tone === 'bad' ? 'border-red-200' : s.tone === 'result' ? 'border-[#436AEB]' : 'border-[#E6EAF5]'}`}>
            <div className="flex items-center gap-2 text-[11px] text-gray-500">
              <span className={`w-4 h-4 rounded-full text-[10px] font-semibold flex items-center justify-center ${s.tone === 'result' ? 'bg-[#436AEB] text-white' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
              {s.title}
            </div>
            <div className={`text-lg font-semibold tabular-nums mt-1 ${s.tone === 'bad' ? 'text-red-700' : s.tone === 'result' ? 'text-[#436AEB]' : 'text-gray-800'}`}>{s.value}</div>
            <div className="text-xs text-gray-500 leading-snug mt-0.5">{s.detail}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Warnings({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="tp-res space-y-1.5">
      {items.map((w) => (
        <li key={w} className="flex gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-500" /> <span>{w}</span>
        </li>
      ))}
    </ul>
  );
}

function Collapse({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {title}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

/* ------------------------------ Önceki aylar grafiği ------------------------------ */

// Recharts eksen yazısını boşluktan kırıyor ("1,4" / "Mn") → tek satır kendi <text>'imiz
function AxisTick({ x, y, payload, f, anchor }) {
  return <text x={x} y={y} dy={3} textAnchor={anchor} fontSize={10} fill="#9CA3AF">{f(payload.value)}</text>;
}

/** Ay ekseni: plan ayı "Plan", geçen yılın aynı ayı turuncu (mevsim oradan okunuyor) */
function MonthTick({ x, y, payload, index, visibleTicksCount, planYm, lyYm }) {
  const m = payload.value;
  const isPlan = m === planYm;
  const isLy = m === lyYm;
  // Dar ekranda 14 ay sığmıyor: plan ve geçen yıl hep, diğerleri üçte bir (geçen yılın komşuları hariç)
  const narrow = typeof window !== 'undefined' && window.innerWidth < 640;
  const mi = (v) => Number(v.slice(0, 4)) * 12 + Number(v.slice(5, 7));
  // geçen yılın iki yanındaki 2 ay ve Plan'dan önceki 3 ay gizli (sağ eksenli grafikler daha dar)
  const nearLy = Math.abs(mi(m) - mi(lyYm)) <= 2 || index >= visibleTicksCount - 4;
  if (narrow && !isPlan && !isLy && (nearLy || (visibleTicksCount - 1 - index) % 3 !== 0)) return <g />;
  const lbl = MONTHS_TR[Number(m.slice(5, 7)) - 1].slice(0, 3);
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={11} dx={isPlan ? 10 : 0} textAnchor={isPlan ? 'end' : 'middle'} fontSize={10} fontWeight={isPlan || isLy ? 600 : 400}
        fill={isPlan ? C.accent : isLy ? '#B45309' : '#9CA3AF'}>{isPlan ? 'Plan' : lbl}</text>
      {((m.slice(5, 7) === '01' && !narrow) || isLy) && !isPlan && (
        <text dy={22} textAnchor="middle" fontSize={9} fill={isLy ? '#B45309' : '#C0C4CC'}>{isLy ? 'geçen yıl' : `'${m.slice(2, 4)}`}</text>
      )}
    </g>
  );
}

function TrendTip({ active, payload, label, rows }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-[#E6EAF5] rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-gray-800 mb-1">{d.plan ? `${monthLabel(label)} · plan` : monthLabel(label)}</div>
      {rows.map(([k, name, f]) => (
        <div key={k} className="flex justify-between gap-4">
          <span className="text-gray-500">{name}</span><span className="tabular-nums text-gray-800">{d[k] == null ? 'veri yok' : f(d[k])}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Son 13 ay + plan ayı. bars: yığılmış çubuklar, line: ikinci eksende çizgi.
 * Plan ayı açık renk + kesikli kenar; geçen yılın aynı ayı eksende turuncu.
 */
function MiniTrend({ data, planYm, bars, line, yFmt = compact, tip, height = 170, legend, lyNote, planLabel }) {
  const lyYm = ymShift(planYm, -12);

  return (
    <div className="tp-res min-w-0">
      {planLabel && <div className="text-[11px] text-right text-gray-500 -mb-1">Plan: <b className="text-[#436AEB]">{planLabel}</b></div>}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 16, right: line ? 4 : 14, left: 0, bottom: 8 }}>
          <CartesianGrid vertical={false} stroke="#EEF1F8" />
          <XAxis dataKey="month" interval={0} height={30} axisLine={false} tickLine={false}
            tick={(pr) => <MonthTick {...pr} planYm={planYm} lyYm={lyYm} />} />
          <YAxis yAxisId="l" width={58} interval={0} tickCount={5} axisLine={false} tickLine={false} tick={<AxisTick f={yFmt} anchor="end" />} />
          {line && <YAxis yAxisId="r" orientation="right" width={52} interval={0} tickCount={5} axisLine={false} tickLine={false} tick={<AxisTick f={line.fmt || compact} anchor="start" />} />}
          <Tooltip cursor={{ fill: '#F3F5FB' }} content={<TrendTip rows={tip} />} />
          {bars.map((b, bi) => (
            <Bar key={b.key} yAxisId="l" dataKey={b.key} stackId="s" maxBarSize={24} isAnimationActive={false}
              radius={bi === bars.length - 1 ? [3, 3, 0, 0] : 0}>
              {data.map((d) => (
                <Cell key={d.month} fill={d.plan ? b.planColor || b.color : b.color} fillOpacity={d.plan ? 0.45 : 1}
                  stroke={d.plan ? b.color : 'none'} strokeDasharray={d.plan ? '3 2' : undefined} />
              ))}
            </Bar>
          ))}
          {line && <Line yAxisId="r" type="monotone" dataKey={line.key} stroke={line.color} strokeWidth={2} isAnimationActive={false}
            dot={(pr) => (pr.value == null || pr.cy == null ? <g key={pr.index} /> : <circle key={pr.index} cx={pr.cx} cy={pr.cy} r={pr.payload.plan ? 4 : 2.5} fill={pr.payload.plan ? '#fff' : line.color} stroke={line.color} strokeWidth={pr.payload.plan ? 2 : 0} />)}
            connectNulls={false} />}
        </ComposedChart>
      </ResponsiveContainer>
      {legend && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 mt-1 pl-2">
          {legend.map(([color, label, kind]) => (
            <span key={label} className="flex items-center gap-1.5">
              <span className={kind === 'line' ? 'w-3 h-0.5' : 'w-2.5 h-2.5 rounded-sm'} style={{ background: color }} />{label}
            </span>
          ))}
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border border-dashed border-[#436AEB] bg-[#DCE4FB]" />plan</span>
          {lyNote && <span className="text-[#B45309]">turuncu ay = geçen yılın aynı ayı, mevsim etkisi buradan okunur</span>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Hedef sayfası şeridi ------------------------------ */

function SavedStrip({ s, planMonth }) {
  if (!s) return null;
  const items = [
    s.revenue != null && `Ciro ${tlc(s.revenue)}`,
    s.transactions != null && `Sipariş ${num(s.transactions)}`,
    s.sessions != null && `Trafik ${compact(s.sessions)}`,
    s.costPerformance != null && `Performans bütçesi ${tlc(s.costPerformance)}`,
    s.costBranding != null && `Branding bütçesi ${tlc(s.costBranding)}`,
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs bg-white border border-[#E6EAF5] rounded-xl px-4 py-2.5">
      <span className="font-medium text-gray-700">Hedef vs Gerçekleşen sayfasında {monthLabel(planMonth)}:</span>
      {items.map((t) => <span key={t} className="text-gray-600 bg-gray-50 rounded px-2 py-0.5">{t}</span>)}
    </div>
  );
}

/* ------------------------------ Özet ------------------------------ */

function Overview({ data, perfOk, perfBudget, total }) {
  const p = data.performance;
  const b = data.branding;
  const planYm = data.planMonth;
  const perfTrend = p?.history?.trend || [];
  const perfBy = Object.fromEntries(perfTrend.map((x) => [x.month, x.cost]));
  const chart = [
    ...b.trend.map((x) => ({ month: x.month, perf: perfBy[x.month] ?? null, brand: x.cost })),
    { month: planYm, plan: true, perf: perfOk ? perfBudget : null, brand: b.totals.budget },
  ].map((x) => ({ ...x, total: (x.perf || 0) + (x.brand || 0) }));
  const last3 = chart.slice(-4, -1);
  const avg3 = last3.reduce((a, x) => a + x.total, 0) / 3;
  const ly = chart.find((x) => x.month === ymShift(planYm, -12));
  const perfShare = total ? ((perfBudget || 0) / total) * 100 : 0;

  return (
    <section className={`${CARD} p-5`}>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="tp-res lg:col-span-2 space-y-4 min-w-0">
          <Stat strong label={`${monthLabel(planYm)} toplam reklam bütçesi`} value={perfOk || p?.reason ? tlc(total) : '—'}
            sub={avg3 ? <>son 3 ay ort. {tlc(avg3)} → <b className="text-gray-700">{dec(total / avg3, 1)} kat</b>{ly?.total ? <> · geçen yıl {shortMonth(ly.month)} {tlc(ly.total)}</> : null}</> : null} />
          {perfOk && (
            <div>
              <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
                <div style={{ width: `${perfShare}%`, background: C.accent }} />
                <div style={{ width: `${100 - perfShare}%`, background: C.brand }} />
              </div>
              <div className="flex justify-between text-xs mt-1.5">
                <span className="text-gray-600"><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: C.accent }} />Performans <b className="tabular-nums">{perfOk ? tlc(perfBudget) : '—'}</b></span>
                <span className="text-gray-600"><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: C.brand }} />Branding <b className="tabular-nums">{tlc(b.totals.budget)}</b></span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#E6EAF5]">
            <Stat label="Site cirosu hedefi" value={p && !p.reason ? tlc(p.targets.revenue) : '—'}
              sub={perfOk ? `₺1 performans harcamasına ₺${dec(p.plan?.siteRoas, 1)} site cirosu` : p?.reason ? 'GA4 cirosu ölçülmüyor' : <span className="text-red-600">geçmiş verimle ulaşılamıyor</span>} />
            <Stat label="Branding erişimi" value={b.totals.reachLow != null ? `${compact(b.totals.reachLow)}–${compact(b.totals.reachHigh)}` : '—'}
              sub="kişi · kanallar arası örtüşme ölçülemiyor" />
          </div>
        </div>
        <div className="lg:col-span-3 min-w-0">
          <div className="text-xs font-semibold text-gray-700 mb-1">Önceki aylarda ne harcandı, plan nereye düşüyor?</div>
          <MiniTrend data={chart} planYm={planYm} height={190} lyNote planLabel={tlc(chart[chart.length - 1].total)}
            bars={p?.reason ? [{ key: 'brand', color: C.brand }] : [{ key: 'perf', color: C.accent }, { key: 'brand', color: C.brand }]}
            tip={p?.reason ? [['brand', 'Branding', tl]] : [['perf', 'Performans', tl], ['brand', 'Branding', tl], ['total', 'Toplam', tl]]}
            legend={p?.reason ? [[C.brand, 'Branding (tüm harcama)']] : [[C.accent, 'Performans'], [C.brand, 'Branding']]} />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Performans ------------------------------ */

const REVENUE_SOURCE = { user: 'sizin girdiğiniz', targets_page: 'Hedef sayfasından', system: 'sistem önerisi' };

function PerformanceBlock({ p, planMonth, inputs, set }) {
  if (p?.reason) {
    return (
      <section className={`${CARD} p-5 space-y-3`}>
        <SectionHead n={1} title="Performans · ciro hedefi" />
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
          {p.note}{p.reason === 'no_revenue_tracking' && ' Trafik ölçülüyor: branding kampanyalarından beklenen site trafiği aşağıdaki branding bölümünde.'}
        </div>
      </section>
    );
  }
  const r = p.required;
  const plan = p.plan;
  const h = p.history;
  const t = p.targets;
  const feas = FEASIBILITY[r.feasibility];
  const oc = p.ordersCheck;
  const ub = p.userBudget;
  const revSuggested = t.revenueSource === 'targets_page' ? t.revenue : p.suggested.revenue;
  const rm = h.months.map(shortMonth);
  const nonAdHist = h.avgSiteRevenue - h.avgAdRevenue;
  const needAd = Math.max(t.needAdRevenue, 0);
  const expRoas = plan?.adRoas ?? (h.adRoas6 != null ? h.adRoas6 * p.season.roasIndex : null);
  const roasBase = h.adRoas6 != null ? h.adRoas6 * p.season.roasIndex : null;
  // Zincir ekranda görünen (yuvarlanmış) rakamlarla kurulur → her adım elle doğrulanabilir
  const nonAdHistS = rMoney(nonAdHist);
  const nonAdS = rMoney(p.expectedNonAd.revenue);
  const needAdS = Math.max(rMoney(t.revenue) - nonAdS, 0);
  const expRoasS = expRoas != null ? rDec(expRoas, 2) : null;
  const budgetS = r.budget != null ? rMoney(r.budget) : null;
  const divEq = budgetS != null && expRoasS ? (rMoney(needAdS / expRoasS) === budgetS ? '=' : '≈') : '≈';
  const lyM = shortMonth(p.season.ly);
  const untagged = h.trend.filter((x) => x.untagged).map((x) => shortMonth(x.month));

  const chart = [
    ...h.trend.map((x) => ({
      month: x.month, cost: x.untagged ? null : x.cost || null, ad: x.untagged ? null : x.adRevenue, siteRevenue: x.siteRevenue,
      nonAd: x.siteRevenue != null ? Math.max(x.siteRevenue - (x.untagged ? 0 : x.adRevenue), 0) : null,
      roas: x.cost && x.siteRevenue && !x.untagged ? x.siteRevenue / x.cost : null,
    })),
    {
      month: planMonth, plan: true, cost: r.unreachable ? null : r.budget, nonAd: p.expectedNonAd.revenue,
      ad: plan ? plan.adRevenue : needAd, siteRevenue: plan ? plan.siteRevenue : t.revenue,
      roas: plan?.siteRoas ?? null,
    },
  ];

  const step4 = r.unreachable
    ? { title: 'Gereken bütçe', value: 'Ulaşılamıyor', tone: 'bad', detail: <>Bütçe son 3 ay ortalamasının 3 katına ({tlp(r.capBudget)}) çıksa bile {r.atCapSiteRevenue != null ? <>site cirosu ~{tlp(r.atCapSiteRevenue)}'de kalıyor.</> : 'trafik hedefi tutmuyor.'}</> }
    : r.coveredWithoutAds
      ? { title: 'Gereken bütçe', value: '₺0', tone: 'result', detail: 'Reklamsız ciro hedefi zaten karşılıyor.' }
      : {
        title: 'Gereken performans bütçesi', value: tlp(r.budget), tone: r.vsMax > 1.5 ? 'bad' : 'result',
        detail: <>
          {r.driver === 'sessions'
            ? <>Ciro için {tlp(r.forRevenue)} yeterdi; trafik hedefi ({compact(t.sessions)} oturum) için {tlp(r.forSessions)} gerekiyor, büyüğü alındı.</>
            : <>{tlp(needAdS)} ÷ {dec(expRoasS)} {divEq} {tlp(r.budget)}.{r.forSessions != null ? ` Trafik hedefi için ${tlp(r.forSessions)} yeterdi.` : ''}</>}
          {' '}Son 3 ay ort. {tlp(h.avgMonthlyCost)}, son 12 ayın en yüksek ayı {tlp(h.maxMonthlyCost)}.
        </>,
      };

  return (
    <section className={`${CARD} p-5 space-y-5`}>
      <SectionHead n={1} title="Performans · site cirosu hedefinden bütçeye"
        sub="Ciro ve trafik site geneli (GA4), Hedef vs Gerçekleşen sayfasıyla aynı tanım"
        right={feas && !r.unreachable && <span className={`text-xs px-2.5 py-1 rounded-full border ${feas.cls}`}>{feas.label}</span>} />

      <div className="flex flex-wrap items-start gap-5">
            <TargetInput label={`${monthLabel(planMonth)} site cirosu hedefi`} prefix="₺" width="w-36"
              value={inputs.revenue} suggested={revSuggested}
              suggestedLabel={t.revenueSource === 'targets_page' ? 'Hedef sayfasından' : 'sistem önerisi'}
              onChange={(v) => set('revenue', v)} />
            <TargetInput label="Site trafiği hedefi" suffix="oturum" width="w-28"
              value={inputs.sessions} suggested={t.sessionsSource === 'targets_page' ? t.sessions : p.suggested.sessions}
              suggestedLabel={t.sessionsSource === 'targets_page' ? 'Hedef sayfasından' : 'sistem önerisi'}
              onChange={(v) => set('sessions', v)} hint={p.sessionsNote} />
            <TargetInput label="Elinizdeki performans bütçesi (isteğe bağlı)" prefix="₺" width="w-32"
              value={inputs.perfBudget} suggested={ub?.source === 'targets_page' ? ub.budget : null}
              suggestedLabel="Hedef sayfasından" onChange={(v) => set('perfBudget', v)}
              hint="Bu bütçeyle ne geleceği de hesaplanır." />
          </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {t.revenueSource === 'system' && (
            <div className="flex gap-2 text-xs text-gray-600 bg-[#F6F8FF] rounded-lg px-3 py-2.5">
              <Info size={13} className="shrink-0 mt-0.5 text-[#436AEB]" />
              <span><b className="text-gray-800">Önerilen hedef neden {tlc(p.suggested.revenue)}?</b> {p.suggested.why}</span>
            </div>
          )}
          {!r.unreachable && (
            <div className="grid grid-cols-2 gap-4 pt-1">
              <Stat strong label="Beklenen site cirosu" value={plan ? tlc(plan.siteRevenue) : '—'}
                sub={plan ? (plan.rangeOk ? `olası ${tlc(plan.siteRevenueLow)} – ${tlc(plan.siteRevenueHigh)}` : 'aralık için veri yetersiz') : null} />
              <Stat label="₺1 performans harcamasına" value={plan ? `₺${dec(plan.siteRoas, 1)}` : '—'} sub={`site cirosu (reklamsız ciro dahil) · son 3 ay ₺${dec(h.siteRoas, 1)}`} />
              <Stat label="Beklenen site trafiği" value={plan?.siteSessions ? compact(plan.siteSessions) : '—'}
                sub={h.avgSiteSessions ? `son 3 ay ort. ${compact(h.avgSiteSessions)}` : null} />
            </div>
          )}
        </div>
        {/* Önceki aylar */}
        <div className="lg:col-span-3 min-w-0">
          <div className="text-xs font-semibold text-gray-700 mb-1">Önceki aylar: site cirosu ve performans harcaması</div>
          <MiniTrend data={chart} planYm={planMonth} planLabel={tlc(chart[chart.length - 1].siteRevenue)}
            bars={[{ key: 'nonAd', color: C.gray }, { key: 'ad', color: C.accent }]}
            line={{ key: 'cost', color: '#111827', fmt: compact }}
            tip={[['siteRevenue', 'Site cirosu', tl], ['ad', 'Performans reklamlarından', tl], ['nonAd', 'Reklam dışı', tl], ['cost', 'Performans harcaması', tl], ['roas', '₺1 performansa site cirosu', (v) => `₺${dec(v, 1)}`]]}
            legend={[[C.gray, 'Reklam dışı ciro'], [C.accent, 'Performans reklamlarından ciro'], ['#111827', 'Performans harcaması (sağ eksen)', 'line']]} />
          {untagged.length > 0 && (
            <p className="text-[11px] text-gray-500 mt-1 pl-2">
              {untagged.join(', ')}: kampanya adlarında "Performance" etiketi yok, performans harcaması ayrılamıyor; bu aylarda yalnız site cirosu gösteriliyor.
            </p>
          )}
        </div>
      </div>

      <Steps title={`${tlc(t.revenue)} site cirosu (${REVENUE_SOURCE[t.revenueSource]}) için bütçe nasıl hesaplandı?`} items={[
        {
          title: 'Reklamsız gelecek ciro', value: tlp(nonAdS),
          detail: <>{rm[0]}–{rm[2]} reklam dışı ciro ort. {tlp(nonAdHistS)} (organik, direkt, branding…) × geçen yıl {lyM} etkisi {facText(lastFactor(nonAdS, nonAdHistS, rMoney))}. Bütçeden bağımsız.</>,
        },
        {
          title: 'Reklamdan gelmesi gereken', value: tlp(needAdS),
          detail: <>Hedef {tlp(rMoney(t.revenue))} − reklamsız {tlp(nonAdS)}. Son 3 ayda bu pay %{h.adShare} idi; planda %{t.revenue ? Math.round((needAd / t.revenue) * 100) : '—'}.</>,
        },
        {
          title: 'Beklenen reklam ROAS\'ı (son tıklama)', value: expRoasS != null ? dec(expRoasS) : '—',
          detail: <>Plandaki kalemlerin son 6 ay ROAS'ı {dec(h.adRoas6)} × {lyM} mevsimi ×{dec(p.season.roasIndex)}{roasBase && expRoasS ? <> × bütçe büyüklüğü ve kalem karışımı {facText(lastFactor(expRoasS, roasBase, (v) => rDec(v, 2)))}</> : null}. Bütçe arttıkça her ek liranın getirisi düşer; verimli kalem biraz daha pay alır.</>,
        },
        step4,
      ]} />

      {r.unreachable && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-900">
          <b>Geçmiş verimle bu hedefe ulaşılamıyor.</b> Hedefi düşürmek ya da reklam dışı satışları (kampanya takvimi, fiyat, CRM) artıracak başka kaldıraçlar gerekir.
        </div>
      )}
      {!r.unreachable && (r.vsMax > 1.1 || plan?.extrapolation) && (
        <Warnings items={[`Bu bütçe son 12 ayın en yüksek ayının (${tlc(h.maxMonthlyCost)}) ${dec(r.vsMax, 1)} katı; o seviyede veri olmadığı için sonuç tahmin değil, ekstrapolasyon.`]} />
      )}

      {ub && (
        <div className="border border-[#DCE4FB] bg-[#F6F8FF] rounded-lg px-4 py-3 text-sm text-gray-800">
          {ub.source === 'targets_page' && inputs.perfBudget == null ? 'Hedef sayfasındaki ' : ''}{tl(ub.budget)} performans bütçesiyle beklenen site cirosu <b>{tlc(ub.siteRevenue)}</b>
          {' '}{ub.rangeOk ? `(aralık ${tlc(ub.siteRevenueLow)} – ${tlc(ub.siteRevenueHigh)})` : ''}{ub.siteSessions ? <>, trafik <b>{compact(ub.siteSessions)}</b> oturum</> : null}.
          {r.budget != null && ub.budget < r.budget && <> Hedef için <b>{tlc(r.budget - ub.budget)}</b> daha gerekiyor.</>}
          {r.budget != null && ub.budget >= r.budget && <> Hedefi karşılıyor.</>}
          {ub.extrapolation && ' (Geçmişte bu bütçe hiç harcanmadı; ekstrapolasyon.)'}
        </div>
      )}

      {oc && (
        <div className="flex flex-wrap items-center justify-between gap-3 border border-[#E6EAF5] rounded-lg px-4 py-3 text-sm text-gray-700">
          <span>
            Hedef sayfasındaki <b>{num(oc.orders)} sipariş</b>, son 3 ayın sepet ortalamasıyla ({tl(oc.aov)}) ≈ <b>{tlc(oc.revenue)}</b> site cirosu demek.{' '}
            {oc.unreachable
              ? <span className="text-red-700">Geçmiş verimle ulaşılamıyor (3 kat bütçeyle ~{tlc(oc.atCapSiteRevenue)}).</span>
              : <>Bunun için ~<b>{tlc(oc.budget)}</b> performans bütçesi gerekir.</>}
          </span>
          <button type="button" onClick={() => set('revenue', Math.round(oc.revenue))} className="text-xs border border-[#E6EAF5] rounded-md px-3 py-1.5 hover:bg-gray-50">Bu ciroyu hedef al</button>
        </div>
      )}

      {plan?.lines?.length > 0 && !r.unreachable && (
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-2">Bütçe nasıl harcanmalı?</div>
          <div className="overflow-x-auto">
            <div className="sm:hidden text-[11px] text-gray-400 mb-1">← tablo yana kaydırılabilir →</div>
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-[#E6EAF5]">
                  <th className="py-2 pr-3 font-medium">Mecra · kampanya türü</th>
                  <th className="py-2 pr-3 font-medium text-right">Bütçe</th>
                  <th className="py-2 pr-3 font-medium text-right">Son 6 ay aylık ort.</th>
                  <th className="py-2 pr-3 font-medium text-right">Reklam ROAS'ı</th>
                  <th className="py-2 pr-3 font-medium text-right">Reklamdan ciro</th>
                  <th className="py-2 pr-3 font-medium text-right">Reklamdan trafik</th>
                </tr>
              </thead>
              <tbody>
                {plan.lines.map((l) => (
                  <tr key={l.key} className="border-b border-gray-50">
                    <td className="py-2 pr-3 text-gray-800">
                      {l.platform} · {l.objective}
                      <div className="h-1 mt-1 rounded-full bg-gray-100 w-32"><div className="h-1 rounded-full bg-[#436AEB]" style={{ width: `${Math.min(100, l.share)}%` }} /></div>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{tl(l.budget)} <span className="text-xs text-gray-400 font-normal">%{dec(l.share, 0)}</span></td>
                    <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{tl(l.historicalAvgMonthly)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{dec(l.forecast.roas)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{tl(l.forecast.revenue)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.forecast.sessions != null ? num(l.forecast.sessions) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">Bütçe kalemlere son 6 aydaki paylarıyla dağıtılır; ROAS'ı yüksek kalemin payı en fazla %30 artar, bir kalem geçmiş ortalamasının en fazla 1,5 katına çıkar.</p>
        </div>
      )}

      {p.excludedLines?.length > 0 && !r.unreachable && (
        <p className="text-xs text-gray-500">
          Plana bütçe ayrılmayan kalemler (son tıklama ROAS'ı 0,5'in altında ya da kampanya türü belirsiz):{' '}
          {p.excludedLines.map((l) => `${l.label} (ROAS ${dec(l.roas)}, aylık ort. ${tlc(l.avgMonthlyCost)})`).join(', ')}.
          {' '}Meta gibi kanallar son tıklamada küçük görünür; bu kalemleri sürdürmek bir marka kararıdır, hedef hesabına katılmadı.
        </p>
      )}

      <Collapse title="Ay ay rakamlar ve varsayımlar">
        <div className="overflow-x-auto">
          <div className="sm:hidden text-[11px] text-gray-400 mb-1">← tablo yana kaydırılabilir →</div>
          <table className="w-full text-xs min-w-[600px]">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[#E6EAF5]">
                <th className="py-1.5 pr-3">Ay</th><th className="py-1.5 pr-3 text-right">Performans harcaması</th>
                <th className="py-1.5 pr-3 text-right">Site cirosu</th><th className="py-1.5 pr-3 text-right">Reklamdan ciro</th>
                <th className="py-1.5 pr-3 text-right">ROAS (site)</th><th className="py-1.5 pr-3 text-right">Site trafiği</th><th className="py-1.5 pr-3 text-right">Sipariş</th>
              </tr>
            </thead>
            <tbody>
              {h.trend.map((m) => (
                <tr key={m.month} className={`border-b border-gray-50 ${m.month === p.season.ly ? 'bg-amber-50' : h.months.includes(m.month) ? 'bg-[#F6F8FF]' : ''}`}>
                  <td className="py-1.5 pr-3">{shortMonth(m.month)}{m.month === p.season.ly && <span className="text-amber-700"> · geçen yıl</span>}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{m.cost ? tl(m.cost) : '—'}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{tl(m.siteRevenue)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{tl(m.adRevenue)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{m.cost && m.siteRevenue ? dec(m.siteRevenue / m.cost, 1) : '—'}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{num(m.siteSessions)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{num(m.transactions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="text-xs text-gray-500 list-disc pl-5 space-y-1 mt-2">
          <li>Mavi zeminli aylar tahminin temeli (son 3 ay); turuncu, mevsim etkisinin okunduğu geçen yılın aynı ayı.</li>
          <li>Mevsim (performans reklamları): {p.season.roasNote}</li>
          <li>Reklamdan ciro ve trafik GA4 son tıklamadır (kampanya adıyla eşlenir). Platform panellerindeki dönüşüm değerinden farklıdır; Meta gibi görüntüleme ağırlıklı kanallar son tıklamada küçük görünür.</li>
        </ul>
      </Collapse>
    </section>
  );
}

/* ------------------------------ Branding ------------------------------ */

function ChannelCard({ c, inputs, set, videos, freqMode, planMonth }) {
  if (!c.available) {
    return (
      <div className="bg-gray-50 border border-[#E6EAF5] rounded-xl p-4">
        <div className="font-medium text-gray-700">{c.label}</div>
        <div className="text-xs text-gray-400 mt-1">{c.note}</div>
      </div>
    );
  }
  const r = c.required;
  const h = c.history;
  const conf = CONFIDENCE[c.curve.confidence];
  const rough = r.rough;
  const fmtF = (v) => dec(v, Number.isInteger(v) ? 0 : 1);
  const to = c.id === 'youtube' ? "YouTube'a" : "Meta'ya";
  const isMeta = c.id === 'meta';
  const perVideo = c.id === 'youtube' && freqMode === 'video' && videos > 1;
  // Meta eğrisi markanın branding karışımından (trafik, etkileşim, erişim) kurulu → "video" denmez
  const what = isMeta ? 'Reklamlarınızı' : videos > 1 ? null : 'Videoyu';
  const sentence = !what && freqMode === 'video'
    ? <>{videos} videonun her birini <b>{compact(c.targets.reach)} kişiye ortalama {fmtF(c.targets.frequency)} kez</b> göstermek için {to} <b>{tlp(r.budget)}</b> gerekir.</>
    : !what
      ? <>{videos} videoyu <b>{compact(c.targets.reach)} kişiye kişi başı toplam ortalama {fmtF(c.targets.frequency)} kez</b> göstermek için {to} <b>{tlp(r.budget)}</b> gerekir.</>
      : <>{what} <b>{compact(c.targets.reach)} kişiye ortalama {fmtF(c.targets.perPersonTotal)} kez</b> göstermek için {to} <b>{tlp(r.budget)}</b> gerekir.</>;
  const cpmSeason = c.season.note ? 1 : c.season.cpm;
  const lyM = shortMonth(c.season.ly);
  const cpmS = rDec(r.cpm, 1);
  const hCpmS = rDec(h.cpm, 1);
  const impS = r.impressions >= 1e6 ? Math.round(r.impressions / 1e4) * 1e4 : r.impressions;
  const budEq = rMoney((impS * cpmS) / 1000) === rMoney(r.budget) ? '=' : '≈';
  const impp = (v) => (v >= 1e6 ? `${(v / 1e6).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mn` : compact(v));
  const budgetX = h.avgMonthlyCost ? r.budget / h.avgMonthlyCost : null;
  const beyondReach = c.targets.reach > c.curve.maxReach;
  const beyond = (budgetX != null && budgetX > 1.5) || beyondReach;

  const chart = [
    ...h.trend.map((x) => ({ ...x, freq: x.reach && x.impressions ? x.impressions / x.reach : null })),
    { month: planMonth, plan: true, cost: r.budget, impressions: r.impressions, reach: c.targets.reach, freq: c.targets.perPersonTotal },
  ];

  return (
    <div className="border border-[#E6EAF5] rounded-xl p-4 space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-semibold text-gray-900">{c.label}</div>
        <span className={`text-xs ${rough ? 'text-red-700' : conf.cls}`}>{rough ? 'Kaba tahmin' : conf.label}{c.curve.mape != null ? ` · geçmiş aylarda ±%${Math.round(c.curve.mape * 100)} sapma` : ''}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 space-y-3">
          <div className="flex flex-wrap items-start gap-4">
            <TargetInput label="Erişim hedefi" suffix="kişi" width="w-28" value={inputs[`${c.id}Reach`]} suggested={c.suggested.reach}
              onChange={(v) => set(`${c.id}Reach`, v)} />
            <TargetInput label={perVideo ? 'Kişi başı her videoyu kaç kez görsün?' : 'Kişi başı kaç kez görsün?'}
              suffix="kez" width="w-14" decimals={1} value={inputs[`${c.id}Freq`]} suggested={c.suggested.frequency}
              onChange={(v) => set(`${c.id}Freq`, v)} />
          </div>
          <div className={`tp-res text-sm text-gray-800 rounded-lg px-3 py-2.5 ${rough ? 'bg-gray-50' : 'bg-[#F6F8FF]'}`}>{sentence}{rough && ' (kaba tahmin)'}</div>
          <div className="grid grid-cols-2 gap-4">
            <Stat strong label="Beklenen erişim" value={`${compact(c.targets.reach)}`}
              sub={r.reachLow != null ? `olası ${compact(r.reachLow)} – ${compact(r.reachHigh)} kişi` : 'aralık için veri yetersiz'} />
            {c.id === 'youtube' && r.views != null
              ? <Stat label="Beklenen izlenme" value={compact(r.views)} sub="son 6 ay izlenme maliyetiyle" />
              : <Stat label="Siteye trafik" value={r.sessions != null ? compact(r.sessions) : '—'} sub="GA4, kampanya adıyla eşleşen" />}
            {perVideo && <Stat label="Video başına" value={tlc(r.perVideo.budget)} sub={`${compact(r.perVideo.impressions)} gösterim, eşit bölünürse`} />}
          </div>
        </div>
        <div className="lg:col-span-3 min-w-0">
          <div className="text-xs font-semibold text-gray-700 mb-1">
            Önceki aylar: harcama ve {isMeta ? 'aylık tekil erişim' : 'en büyük kampanyanın erişimi'}
          </div>
          <MiniTrend data={chart} planYm={planMonth} height={160} planLabel={tlp(r.budget)}
            bars={[{ key: 'cost', color: isMeta ? '#7C8DB5' : '#E5484D' }]}
            line={{ key: 'reach', color: C.accent, fmt: compact }}
            yFmt={(v) => `₺${compact(v)}`}
            tip={[['cost', 'Harcama', tl], ['impressions', 'Gösterim', num], ['reach', isMeta ? 'Tekil erişim' : 'En büyük kampanyanın erişimi', num], ['freq', 'Kişi başı gösterim', (v) => dec(v, 1)]]}
            legend={[[isMeta ? '#7C8DB5' : '#E5484D', 'Harcama'], [C.accent, isMeta ? 'Tekil erişim (sağ eksen)' : 'Erişim (sağ eksen)', 'line']]} />
        </div>
      </div>

      <Steps items={[
        {
          title: 'Gereken gösterim', value: impp(r.impressions),
          detail: r.impBasis === 'frequency'
            ? <>{num(c.targets.reach)} kişi × kişi başı {fmtF(c.targets.perPersonTotal)} gösterim{perVideo ? ` (${videos} video × ${fmtF(c.targets.frequency)})` : ''}.</>
            : <>Kişi başı {fmtF(c.targets.perPersonTotal)} gösterim {num(c.targets.reach)} kişiye ulaşmaya yetmiyor: geçmiş erişim eğrisine göre bu kadar kişi için {impp(r.impressions)} gösterim gerekiyor (kişi başı ~{dec(r.impressions / c.targets.reach, 2)}). Bütçe eğriye göre kuruldu.</>,
        },
        {
          title: 'Beklenen CPM (1.000 gösterim)', value: cpmFmt(cpmS),
          detail: <>Son 3 ay CPM {cpmFmt(hCpmS)}{c.season.note ? null : <> × geçen yıl {lyM} etkisi ×{dec(c.season.cpm)}</>} × bütçe büyüdükçe artış {facText(lastFactor(cpmS, hCpmS * cpmSeason, (v) => rDec(v, 1)))}.{c.season.note ? ` ${c.season.note}` : ''}</>,
        },
        {
          title: rough ? 'Gereken bütçe (kaba tahmin)' : 'Gereken bütçe', value: tlp(r.budget), tone: 'result',
          detail: <>{impp(impS)} gösterim × {cpmFmt(cpmS)} ÷ 1.000 {budEq} {tlp(r.budget)}{perVideo ? `; video başına ${tlp(r.perVideo.budget)}` : ''}.</>,
        },
        {
          title: 'Geçmişle kıyas', value: budgetX != null ? `${dec(budgetX, 1)} kat` : '—', tone: beyond ? 'bad' : undefined,
          detail: <>
            Son 3 ay aylık ort. {tlp(h.avgMonthlyCost)}. Geçmişte {isMeta ? 'bir ayda' : 'tek kampanyayla'} ulaşılan en yüksek erişim {compact(c.curve.maxReach)} kişi.
            {beyond && <b className="text-red-700"> Plan geçmişte denenmiş seviyenin dışında{beyondReach ? ' (erişim hedefi en yüksek erişimin üstünde)' : ''}; rakam tahminden çok ekstrapolasyon.</b>}
          </>,
        },
      ]} />

      <Warnings items={beyond ? c.warnings.filter((w) => !w.startsWith('Gereken bütçe son 3 ay') && !w.startsWith('Girilen erişim, geçmişte')) : c.warnings} />
      {c.notes?.map((n) => (
        <p key={n} className="flex gap-2 text-xs text-gray-500"><Info size={13} className="shrink-0 mt-0.5 text-gray-400" />{n}</p>
      ))}

      <Collapse title="Ay ay rakamlar ve varsayımlar">
        <div className="overflow-x-auto">
          <div className="sm:hidden text-[11px] text-gray-400 mb-1">← tablo yana kaydırılabilir →</div>
          <table className="w-full text-xs min-w-[520px]">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[#E6EAF5]">
                <th className="py-1.5 pr-3">Ay</th>
                <th className="py-1.5 pr-3 text-right">Harcama</th>
                <th className="py-1.5 pr-3 text-right">Gösterim</th>
                <th className="py-1.5 pr-3 text-right">CPM</th>
                <th className="py-1.5 pr-3 text-right">{isMeta ? 'Tekil erişim' : 'En büyük kampanyanın erişimi'}</th>
                <th className="py-1.5 pr-3 text-right">Kişi başı gösterim</th>
              </tr>
            </thead>
            <tbody>
              {h.trend.map((m) => (
                <tr key={m.month} className={`border-b border-gray-50 ${m.month === c.season.ly ? 'bg-amber-50' : h.months.includes(m.month) ? 'bg-[#F6F8FF]' : ''}`}>
                  <td className="py-1.5 pr-3">{shortMonth(m.month)}{m.month === c.season.ly && <span className="text-amber-700"> · geçen yıl</span>}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{tl(m.cost)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{num(m.impressions)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{m.cost && m.impressions ? tl((m.cost / m.impressions) * 1000) : '—'}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{num(m.reach)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{isMeta && m.reach && m.impressions ? dec(m.impressions / m.reach, 1) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="text-[11px] text-gray-500 list-disc pl-5 space-y-1 mt-2">
          {isMeta
            ? <li>Meta tekil erişimi hesap seviyesinde ölçüyor; aynı kişi birden çok kampanyada görse de bir kez sayılır (en fazla 13 ay geriye). Tahmin markanın mevcut branding kampanya karışımına (trafik, etkileşim, erişim) dayanır; yalnız erişim amaçlı kampanya açılırsa CPM düşer, erişim artar.</li>
            : <li>Google hesap genelinde tekil erişim vermiyor; kampanyaların erişimi toplanınca aynı kişi birden çok sayılır. Tahmin tek kampanyaların geçmişinden yapılır ve videoların tek bir erişim kampanyasında yayınlanacağını varsayar. Sabit CPM'li rezervasyonlar hariç. Kişi başı gösterim bu yüzden aylık olarak verilmez.</li>}
          <li>Erişim eğrisi: {c.curve.points} geçmiş {c.curve.basis === 'channel_month' ? 'ay' : 'kampanya-ay'} noktası{c.curve.r2 != null ? `, R² ${dec(c.curve.r2)}` : ''}{c.curve.points < 6 ? ' (6\'dan az nokta: düşük güven)' : ''}. Sapma, son 6 ayın her biri o ay dışarıda bırakılarak tahmin edilip gerçekle kıyaslanarak ölçüldü. Erişim, Türkiye'de bu platformun reklamla ulaşabildiği ~{compact(c.universe)} kişide doyar (varsayım: DataReportal 2025).</li>
          <li>{c.season?.note || `CPM mevsimi: geçen yıl ${lyM}'te CPM önceki 3 ayın ×${dec(c.season.cpmRaw)} katıydı${c.season.cpmRaw !== c.season.cpm ? `; tahminde ×${dec(c.season.cpm)} ile sınırlandı` : ''}.`}</li>
        </ul>
      </Collapse>
    </div>
  );
}

function OtherCard({ o, inputs, set }) {
  if (!o) return null;
  return (
    <div className="border border-[#E6EAF5] rounded-xl p-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-semibold text-gray-900">{o.label}</div>
        <span className="text-xs text-gray-400">Erişim hedefi verilmez · son 3 ay ortalaması × mevsim</span>
      </div>
      <div className="flex flex-wrap items-end gap-5">
        <TargetInput label="Bütçe" prefix="₺" value={inputs.otherBudget} suggested={o.suggested.budget} onChange={(v) => set('otherBudget', v)} />
        <div className="text-sm text-gray-700 pb-1.5">
          ≈ <b className="tabular-nums">{compact(o.impressions)}</b> gösterim{o.sessions != null && <> · <b className="tabular-nums">{compact(o.sessions)}</b> oturum</>}
          <span className="text-gray-400 text-xs"> (son 3 ay ort. {tlc(o.avgMonthlyCost)})</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
        {o.lines.map((l) => <span key={l.label} className="bg-gray-50 rounded px-2 py-0.5">{l.label}: {tlc(l.avgMonthlyCost)}/ay</span>)}
      </div>
      <p className="text-xs text-gray-400">Adında "Branding" geçen ama erişim amaçlı olmayan kampanyalar (mağaza ziyareti, Haritalar, tıklama odaklı görüntülü, rezervasyon…). Erişim eğrisine katılmaz; branding bütçesinin eksik kalmaması için ayrı planlanır. Performans tablosundaki Performance Max / Demand Gen satırları adında "Performance" geçen ayrı kampanyalardır; aynı harcama iki yerde sayılmaz.</p>
    </div>
  );
}

function BrandingBlock({ b, planMonth, inputs, set }) {
  const t = b.totals;
  const sc = b.savedCheck;
  const ss = b.spendSeason;
  return (
    <section className={`${CARD} p-5 space-y-5`}>
      <SectionHead n={2} title="Branding · erişim hedefinden bütçeye" sub="Erişim = reklamı en az bir kez gören tekil kişi" />

      <div className="flex flex-wrap items-end gap-5">
        <TargetInput label="YouTube'da kaç video yayınlanacak?" suffix="video" width="w-12" value={inputs.videos ?? 1} suggested={null}
          onChange={(v) => set('videos', v)} />
        {b.videos > 1 && (
          <label className="flex flex-col text-xs text-gray-500 gap-1">
            YouTube'da sıklık nasıl sayılsın?
            <div className="flex flex-wrap rounded-md border border-[#E6EAF5] overflow-hidden text-sm">
              {[['video', `Her kişi her videoyu ayrı ayrı görsün`], ['total', `${b.videos} videodan toplamda`]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => set('freqMode', k)}
                  className={`px-3 py-1.5 ${b.freqMode === k ? 'bg-[#436AEB] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{label}</button>
              ))}
            </div>
          </label>
        )}
        {b.videos > 1 && (
          <span className="text-xs text-gray-400 max-w-md leading-snug pb-1">
            {b.freqMode === 'video'
              ? `3 girerseniz: her kişi ${b.videos} videonun her birini 3'er kez görür, kişi başı toplam ${b.videos * 3} gösterim.`
              : `3 girerseniz: her kişi ${b.videos} video arasından toplam 3 gösterim görür; bazı videoları hiç görmeyebilir.`}
          </span>
        )}
      </div>

      <div className="flex gap-2 text-xs text-gray-600 bg-[#F6F8FF] rounded-lg px-3 py-2.5">
        <Info size={13} className="shrink-0 mt-0.5 text-[#436AEB]" />
        <span>
          <b className="text-gray-800">Önerilen erişim hedefleri nasıl bulundu?</b>{' '}
          {ss?.note || `Geçen yıl ${MONTHS_TR[Number(ss.ly.slice(5, 7)) - 1]} ayında branding harcaması önceki 3 ayın ${dec(ss.raw, 1)} katıydı${ss.raw !== ss.ratio ? `; öneride en fazla ${dec(ss.ratio, 1)} kat alındı` : ''}. Her kanalın son 3 ay ortalama harcaması bu oranla çarpıldı, o bütçeyle kaç kişiye ulaşılacağı geçmiş erişim eğrisinden okundu. Öneri, geçmişte ulaşılan en yüksek erişimi aşmaz.`}
        </span>
      </div>

      <div className="space-y-4">
        {b.channels.map((c) => <ChannelCard key={c.id} c={c} inputs={inputs} set={set} videos={b.videos} freqMode={b.freqMode} planMonth={planMonth} />)}
        <OtherCard o={b.other} inputs={inputs} set={set} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-[#E6EAF5]">
        <Stat strong label={`${monthLabel(planMonth)} branding bütçesi`} value={tlc(t.budget)}
          sub={b.savedBudget != null ? `Hedef sayfasında ${tlc(b.savedBudget)} · önerilen ${tlc(t.suggestedBudget)}` : `önerilen ${tlc(t.suggestedBudget)}`} />
        <Stat label="Toplam erişim" value={t.reachLow != null ? `${compact(t.reachLow)} – ${compact(t.reachHigh)}` : '—'}
          sub="en az – en fazla kişi: aynı kişi YouTube ve Meta'da görebilir" />
        <Stat label="Toplam gösterim" value={compact(t.impressions)} sub={t.views != null ? `${compact(t.views)} YouTube izlenme` : null} />
        <Stat label="Siteye trafik (branding)" value={t.sessions != null ? compact(t.sessions) : '—'} sub="GA4, kampanya adıyla eşleşen" />
      </div>

      {sc && (
        <div className="border border-[#DCE4FB] bg-[#F6F8FF] rounded-lg px-4 py-3 text-sm text-gray-800">
          Hedef sayfasındaki <b>{tlc(sc.budget)}</b> branding bütçesi bu plandaki oranlarla dağıtılırsa:{' '}
          {sc.channels.map((c, i) => <span key={c.id}>{i ? ', ' : ''}{c.label} {tlc(c.budget)} → ~<b>{compact(c.reach)} kişi</b></span>)}
          {sc.other != null && <>, diğer kalemler {tlc(sc.other)}</>}.
        </div>
      )}
    </section>
  );
}

/* ------------------------------ Hedef sayfasına yaz ------------------------------ */

// Birbirine bağlı hedefler birlikte yazılır ya da birlikte kalır (bütçe ↔ ciro ↔ ROAS; bütçe ↔ gösterim ↔ izlenme)
const GROUP_OF = {
  cost_performans: 'perf', revenue_performans: 'perf', roas_performans: 'perf',
  cost_branding: 'brand', impressions_branding: 'brand', views_branding: 'brand',
};

const WRITE_FIELDS = [
  ['cost_performans', 'Performans bütçesi', tl],
  ['revenue_performans', 'Performans cirosu (reklamdan, son tıklama)', tl],
  ['roas_performans', 'Reklam ROAS\'ı (son tıklama; Hedef sayfasında gösterilmiyor)', (v) => dec(v)],
  ['cost_branding', 'Branding bütçesi', tl],
  ['impressions_branding', 'Branding gösterim', num],
  ['views_branding', 'Branding izlenme', num],
];

function SaveDialog({ rows, planMonth, onCancel, onConfirm, saving, onToggle }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-5 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-semibold text-gray-800">{monthLabel(planMonth)} · Hedef vs Gerçekleşen sayfasına yazılacak</h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="overflow-x-auto">
          <div className="sm:hidden text-[11px] text-gray-400 mb-1">← tablo yana kaydırılabilir →</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b"><th className="py-1.5 pr-2" /><th className="py-1.5 pr-3">Hedef</th><th className="py-1.5 pr-3 text-right">Şu an</th><th className="py-1.5 text-right">Yeni</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.key}>
                  <tr className="border-b border-gray-50">
                    <td className="py-1.5 pr-2"><input type="checkbox" checked={r.on} onChange={() => onToggle(r.key)} /></td>
                    <td className="py-1.5 pr-3 text-gray-700">{r.label}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">{r.old == null ? 'boş' : r.fmt(r.old)}</td>
                    <td className={`py-1.5 text-right tabular-nums ${!r.on ? 'text-gray-300 line-through' : r.old !== r.next ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{r.fmt(r.next)}</td>
                  </tr>
                  {r.why && <tr><td /><td colSpan={3} className="pb-1.5 text-[11px] text-amber-700">{r.why}</td></tr>}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500">
          Bağlı hedefler birlikte seçilir: performans bütçesi, cirosu ve ROAS'ı; branding bütçesi, gösterimi ve izlenmesi.
          Site cirosu, trafik, sipariş, toplam bütçe ve ROAS (site) hedeflerine dokunulmaz. Erişim hedefleri yazılmaz: Hedef sayfasında erişim
          kolonu yok; kanal bazlı Pacing hedeflerine de yazılmıyor, aynı hedef iki yerde tutulmasın diye.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-sm border border-gray-200 px-3 py-1.5 rounded-md hover:bg-gray-50">Vazgeç</button>
          <button type="button" onClick={onConfirm} disabled={saving || !rows.some((r) => r.on)}
            className="flex items-center gap-1.5 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Yaz
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Sekme ------------------------------ */

const EMPTY_INPUTS = { revenue: null, sessions: null, perfBudget: null, videos: null, freqMode: 'video', otherBudget: null };

export default function TargetPlanTab({ brandId }) {
  const [month, setMonth] = useState(nextMonthYm());
  const [inputs, setInputs] = useState(EMPTY_INPUTS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState(null);
  const reqId = useRef(0);

  const set = useCallback((k, v) => setInputs((s) => ({ ...s, [k]: v })), []);

  const load = useCallback(async (m, inp) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ month: m });
      Object.entries(inp).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, v); });
      const json = await call(`/api/yatas-planning/target-plan?${qs}`, brandId);
      if (id === reqId.current) { setData(json); setError(null); }
    } catch (e) {
      if (id === reqId.current) setError(e.message);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    const t = setTimeout(() => load(month, inputs), data ? 500 : 0);
    return () => clearTimeout(t);
  }, [month, inputs]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeMonth = (m) => { setInputs(EMPTY_INPUTS); setData(null); setMonth(m); };

  const p = data?.performance;
  const b = data?.branding;
  const perfOk = p && !p.reason && !p.required.unreachable;
  const perfBudget = perfOk ? p.required.budget : null;
  const total = (perfBudget || 0) + (b?.totals?.budget || 0);

  const openSave = async () => {
    if (!data) return;
    const y = Number(data.planMonth.slice(0, 4));
    const mo = Number(data.planMonth.slice(5, 7));
    try {
      const cur = (await call(`/api/yatas-kpi/targets?year=${y}`, brandId)).targets?.[mo] || {};
      const next = {
        cost_branding: b.totals.budget,
        impressions_branding: b.totals.impressions,
        ...(b.totals.views != null ? { views_branding: b.totals.views } : {}),
        ...(perfOk ? {
          cost_performans: perfBudget,
          revenue_performans: p.plan.adRevenue,
          roas_performans: p.plan.adRoas,
        } : {}),
      };
      // Kayıtlı bütçe hedefini DÜŞÜREN satırlar varsayılan olarak işaretsiz: müşterinin kendi kararını sessizce ezme
      const lowers = (k) => (k === 'cost_branding' || k === 'cost_performans') && cur[k] != null && next[k] < cur[k];
      const groupOff = { perf: lowers('cost_performans'), brand: lowers('cost_branding') };
      const rows = WRITE_FIELDS.filter(([k]) => k in next).map(([key, label, fmt]) => ({
        key, label, fmt, group: GROUP_OF[key], old: cur[key] ?? null, next: next[key], on: !groupOff[GROUP_OF[key]],
        why: lowers(key)
          ? `Plan kayıtlı bütçenizden ${tlc(cur[key] - next[key])} düşük. İşaretlemezseniz bu bütçe ve ona bağlı ${GROUP_OF[key] === 'perf' ? 'ciro ve ROAS' : 'gösterim ve izlenme'} hedefleri olduğu gibi kalır.`
          : null,
      }));
      setDialog({ y, mo, cur, next, rows });
    } catch (e) {
      toast.error('Hedef sayfası okunamadı: ' + e.message);
    }
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      // PUT tüm kolonları yazar → mevcut satır korunur, yalnız seçili kolonlar değişir
      const chosen = Object.fromEntries(dialog.rows.filter((r) => r.on).map((r) => [r.key, r.next]));
      // Reklam ROAS'ı yalnız performans bütçesi ve cirosuyla birlikte yazılır
      if (!chosen.cost_performans || !chosen.revenue_performans) delete chosen.roas_performans;
      const targets = { ...dialog.cur, ...chosen };
      delete targets.notes;
      await call('/api/yatas-kpi/targets', brandId, { method: 'PUT', body: JSON.stringify({ year: dialog.y, month: dialog.mo, targets }) });
      toast.success('Hedef sayfasına yazıldı');
      setDialog(null);
    } catch (e) {
      toast.error('Kaydedilemedi: ' + e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className={`space-y-5 pb-24 ${loading && data ? 'tp-stale' : ''}`}>
      {/* Yeniden hesap sürerken eski sonuç soluk: girilen hedefle uyuşmayan rakam okunmasın */}
      <style>{'.tp-res{transition:opacity .15s}.tp-stale .tp-res{opacity:.3}'}</style>
      {loading && data && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2 rounded-full shadow-lg">
          <Loader2 size={14} className="animate-spin" /> Yeniden hesaplanıyor…
        </div>
      )}
      <div className={`${CARD} p-4 flex flex-wrap items-center justify-between gap-3`}>
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <select value={month} onChange={(e) => changeMonth(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800 bg-white">
            {[1, 2, 3].map((k) => nextMonthYm(k)).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <span className="text-sm text-gray-500">Sistem hedefi önerir; değiştirdiğiniz her hedefe göre gereken bütçe yeniden hesaplanır.</span>
          {loading && data && <Loader2 size={14} className="animate-spin text-gray-400" />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setInputs({ ...EMPTY_INPUTS, freqMode: inputs.freqMode })} disabled={!data}
            className="flex items-center gap-1.5 text-sm border border-gray-200 px-3 py-1.5 rounded-md hover:bg-gray-50 disabled:opacity-50">
            <RotateCcw size={14} /> Önerilere dön
          </button>
          <button onClick={openSave} disabled={!data || saving || loading}
            className="flex items-center gap-1.5 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50">
            <Save size={14} /> Hedef sayfasına yaz
          </button>
        </div>
      </div>

      {!data && loading && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-2 text-center">
          <Loader2 className="animate-spin" />
          Meta ve Google'dan geçmiş erişim verisi çekiliyor (ilk açılışta ~20 sn)…
        </div>
      )}

      {error && !loading && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-900">
          <span>Plan hesaplanamadı{data ? '; aşağıda son başarılı hesap görünüyor' : ''}. ({error})</span>
          <button type="button" onClick={() => load(month, inputs)} className="text-sm border border-red-200 bg-white px-3 py-1.5 rounded-md hover:bg-red-50">Tekrar dene</button>
        </div>
      )}

      {data && (
        <>
          <SavedStrip s={data.savedTargets} planMonth={data.planMonth} />
          <Overview data={data} perfOk={perfOk} perfBudget={perfBudget} total={total} />

          <PerformanceBlock p={p} planMonth={data.planMonth} inputs={inputs} set={set} />
          <BrandingBlock b={b} planMonth={data.planMonth} inputs={inputs} set={set} />

          {data.reachSources?.some((s) => s.error) && (
            <p className="text-xs text-gray-400">
              Veri kaynağı uyarısı: {data.reachSources.filter((s) => s.error).map((s) => `${s.platform} ${s.account}: ${s.error}`).join(' · ')}
            </p>
          )}
        </>
      )}

      {dialog && <SaveDialog rows={dialog.rows} planMonth={data.planMonth} saving={saving} onCancel={() => setDialog(null)} onConfirm={confirmSave}
        onToggle={(k) => setDialog((d) => {
          const g = d.rows.find((r) => r.key === k)?.group;
          const on = !d.rows.find((r) => r.key === k)?.on;
          return { ...d, rows: d.rows.map((r) => (r.group === g ? { ...r, on } : r)) };
        })} />}
    </div>
  );
}
